// screens/marketplace/MarketplaceCategories.jsx
import React, { useCallback, useEffect, useState } from "react";
import { Tags, Trash2, Pencil, Plus, X } from "lucide-react";
import { useAuth } from "../../providers/AuthProvider";
import { useToast } from "../../providers/ToastProvider";
import {
  PageShell,
  Section,
  Button,
  Field,
  Input,
  Textarea,
  FormGrid,
  GridSpan,
  EmptyState,
  LoadingBlock,
  parseApiError,
  useTokens,
} from "./shared";

const EMPTY = { name: "", slug: "", description: "" };

export default function MarketplaceCategories() {
  const { authFetch } = useAuth();
  const { showToast } = useToast();
  const t = useTokens();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null); // category being edited
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await authFetch("/marketplace/categories/");
      const data = await res.json();
      if (res.ok) setCategories(data.data || []);
      else showToast(parseApiError(data), "error");
    } catch {
      showToast("Could not load categories", "error");
    } finally {
      setLoading(false);
    }
  }, [authFetch, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const reset = () => {
    setForm(EMPTY);
    setEditing(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const url = editing
        ? `/marketplace/categories/${editing.id}/`
        : "/marketplace/categories/";
      const method = editing ? "PATCH" : "POST";
      const res = await authFetch(url, {
        method,
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(parseApiError(data));
      showToast(editing ? "Category updated" : "Category created", "success");
      reset();
      load();
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (cat) => {
    setEditing(cat);
    setForm({
      name: cat.name || "",
      slug: cat.slug || "",
      description: cat.description || "",
    });
  };

  const remove = async (cat) => {
    if (!window.confirm(`Delete "${cat.name}"? Products in it will keep existing.`))
      return;
    try {
      const res = await authFetch(`/marketplace/categories/${cat.id}/`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(parseApiError(d));
      }
      showToast("Category deleted", "success");
      load();
    } catch (err) {
      showToast(err.message, "error");
    }
  };

  return (
    <PageShell
      eyebrow="Catalogue"
      title="Categories"
      subtitle="Groups customers use to browse products."
    >
      <Section
        title={editing ? `Editing "${editing.name}"` : "New category"}
        icon={editing ? Pencil : Plus}
        chip={editing ? "Edit mode" : undefined}
        footer={
          <>
            {editing && (
              <Button variant="ghost" onClick={reset} icon={X}>
                Cancel edit
              </Button>
            )}
            <Button
              icon={editing ? Pencil : Plus}
              onClick={submit}
              disabled={busy || !form.name.trim() || !form.slug.trim()}
            >
              {busy ? "Saving…" : editing ? "Save changes" : "Create category"}
            </Button>
          </>
        }
      >
        <form onSubmit={submit}>
          <FormGrid columns={3}>
            <Field label="Name">
              <Input
                required
                placeholder="e.g. Fashion"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Field>
            <Field label="URL slug">
              <Input
                required
                placeholder="e.g. fashion"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
              />
            </Field>
            <GridSpan span="full">
              <Field label="Description" hint="Optional. Shown on the category page.">
                <Textarea
                  placeholder="What kind of products live here?"
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                />
              </Field>
            </GridSpan>
          </FormGrid>
        </form>
      </Section>

      <Section
        title="All categories"
        icon={Tags}
        chip={`${categories.length}`}
      >
        {loading ? (
          <LoadingBlock label="Loading categories…" />
        ) : categories.length === 0 ? (
          <EmptyState
            icon={Tags}
            title="No categories yet"
            description="Create the first one using the form above."
          />
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            {categories.map((cat) => (
              <div
                key={cat.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  padding: 14,
                  border: `1px solid ${t.border}`,
                  borderRadius: 12,
                  background: t.surfaceRaised,
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    background: t.subtle,
                    color: t.accent,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Tags size={18} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: t.heading,
                    }}
                  >
                    {cat.name}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: t.muted,
                      marginTop: 2,
                    }}
                  >
                    /{cat.slug} · {cat.products_count ?? 0} products
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="secondary"
                  icon={Pencil}
                  onClick={() => startEdit(cat)}
                >
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="dangerGhost"
                  icon={Trash2}
                  onClick={() => remove(cat)}
                >
                  Delete
                </Button>
              </div>
            ))}
          </div>
        )}
      </Section>
    </PageShell>
  );
}